import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { CardArt } from "@/components/OrderCard";
import { Heart, Plus, Trash2, BellPlus } from "lucide-react";
import { useWishlist, removeFromWishlist } from "@/lib/wishlist";
import { useIsSignedIn } from "@/lib/auth-context";

export const Route = createFileRoute("/_authenticated/wishlist")({
  head: () => ({ meta: [{ title: "Watchlist — Want To Buy" }] }),
  component: WishlistPage,
});

function hashSeed(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 100;
}

function WishlistPage() {
  const items = useWishlist();
  const signedIn = useIsSignedIn();

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Heart className="h-7 w-7 text-primary" /> Watchlist
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Buy orders you've saved by tapping the heart icon. Jump back in any time.
          </p>
        </div>
      </div>

      {!signedIn && items.length === 0 && (
        <>
          <div className="panel p-10 text-center">
            <Heart className="mx-auto h-10 w-10 text-primary" />
            <h2 className="mt-3 font-display text-xl font-bold">Sign in to start your watchlist</h2>
            <p className="mt-1 text-sm text-muted-foreground">Save buy orders you want to keep an eye on and jump back in any time.</p>
            <Link to="/auth" className="btn-primary mt-4 inline-flex px-5 py-2 text-sm">Sign in</Link>
          </div>

          <div className="mt-8">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Preview — what a populated watchlist looks like</div>
            <div className="grid gap-4 opacity-50 pointer-events-none select-none sm:grid-cols-2 lg:grid-cols-3">
              {[
                { name: "Charizard", tcg: "Pokémon", condition: "PSA 9" },
                { name: "Monkey D. Luffy (Leader)", tcg: "One Piece", condition: "Near Mint" },
                { name: "Black Lotus", tcg: "Magic: The Gathering", condition: "BGS 7" },
              ].map((c, i) => (
                <div key={i} className="panel p-4">
                  <CardArt seed={hashSeed(c.name + i)} />
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.tcg}</div>
                    <h3 className="font-display text-lg font-bold leading-tight">{c.name}</h3>
                    <p className="text-xs text-muted-foreground">{c.condition}</p>
                  </div>
                  <button
                    disabled
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md py-2 text-sm font-semibold"
                    style={{ background: "color-mix(in oklab, var(--color-primary) 35%, transparent)", color: "color-mix(in oklab, var(--color-primary-foreground, white) 70%, transparent)" }}
                  >
                    <Plus className="h-4 w-4" /> Post buy order
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {items.length === 0 && signedIn && (
        <div className="panel p-10 text-center">
          <p className="text-sm text-muted-foreground">No saved orders yet — heart any buy order to save it here.</p>
          <Link to="/" hash="open-orders" className="btn-primary mt-4 inline-flex px-5 py-2 text-sm">Browse Orders</Link>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((w) => (
          <div key={w.key} className="panel p-4">
            <CardArt seed={hashSeed(w.key)} />
            <div className="mt-3 flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{w.tcg}</div>
                <h3 className="font-display text-lg font-bold leading-tight">{w.cardName}</h3>
                <p className="text-xs text-muted-foreground">{w.set ?? ""}{w.cardNumber ? ` · #${w.cardNumber}` : ""}</p>
              </div>
              <button
                onClick={() => removeFromWishlist(w.key)}
                aria-label="Remove from wishlist"
                title="Remove"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-border bg-background/60 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <Link
              to="/create-order"
              className="btn-primary mt-3 inline-flex w-full items-center justify-center gap-2 py-2 text-sm"
            >
              <Plus className="h-4 w-4" /> Post buy order
            </Link>
            <Link
              to="/price-alerts"
              search={{ prefill: 1, cardName: w.cardName, tcg: w.tcg }}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 text-xs text-primary hover:underline"
            >
              <BellPlus className="h-3.5 w-3.5" /> Create alert for similar cards
            </Link>
          </div>
        ))}
      </div>
    </AppShell>
  );
}
